import {
  addDoc,
  collection,
  doc,
  getDoc,
  onSnapshot,
  Timestamp,
  updateDoc
} from 'firebase/firestore';
import { Card, GamePhase, Team } from '../types';
import { db } from './config';

export interface FirebaseGameData {
  id: string;
  cards: Card[];
  currentTeam: Team;
  gamePhase: GamePhase;
  redCards: number;
  blueCards: number;
  winner: Team | null;
  gameStartTime: Timestamp;
  selectedLanguageSetId: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export class GameService {

  // Create a new game in Firebase
  static async createGame(
    cards: Card[],
    selectedLanguageSetId: string
  ): Promise<string> {
    try {
      const gameData: Omit<FirebaseGameData, 'id'> = {
        cards,
        currentTeam: 'red',
        gamePhase: 'playing',
        redCards: 9,
        blueCards: 8,
        winner: null,
        gameStartTime: Timestamp.now(),
        selectedLanguageSetId,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      };

      const docRef = await addDoc(collection(db, 'games'), gameData);
      return docRef.id;
    } catch (error) {
      console.error('Error creating game:', error);
      throw new Error('Failed to create game');
    }
  }

  // Get game data by ID
  static async getGame(gameId: string): Promise<FirebaseGameData | null> {
    try {
      const docRef = doc(db, 'games', gameId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        return {
          id: docSnap.id,
          ...docSnap.data()
        } as FirebaseGameData;
      } else {
        return null;
      }
    } catch (error) {
      console.error('Error getting game:', error);
      return null;
    }
  }

  // Update card state when a card is clicked
  static async updateCardState(
    gameId: string,
    cardIndex: number,
    cards: Card[],
    currentTeam: Team,
    redCards: number,
    blueCards: number,
    gamePhase: GamePhase,
    winner: Team | null
  ): Promise<void> {
    try {
      const docRef = doc(db, 'games', gameId);
      await updateDoc(docRef, {
        cards,
        currentTeam,
        redCards,
        blueCards,
        gamePhase,
        winner,
        updatedAt: Timestamp.now()
      });
    } catch (error) {
      console.error('Error updating card state:', error);
      throw new Error('Failed to update game state');
    }
  }

  // Listen to real-time game updates
  static subscribeToGame(
    gameId: string,
    callback: (gameData: FirebaseGameData | null) => void
  ): () => void {
    const docRef = doc(db, 'games', gameId);

    return onSnapshot(docRef, (doc) => {
      if (doc.exists()) {
        const gameData = {
          id: doc.id,
          ...doc.data()
        } as FirebaseGameData;
        callback(gameData);
      } else {
        callback(null);
      }
    }, (error) => {
      console.error('Error listening to game updates:', error);
      callback(null);
    });
  }

  // Update current team (for end turn)
  static async updateCurrentTeam(gameId: string, currentTeam: Team): Promise<void> {
    try {
      const docRef = doc(db, 'games', gameId);
      await updateDoc(docRef, {
        currentTeam,
        updatedAt: Timestamp.now()
      });
    } catch (error) {
      console.error('Error updating current team:', error);
      throw new Error('Failed to update current team');
    }
  }
}
